/**
 * InvoiceApprovalSettingsSection Component
 *
 * Invoice approval drawn as its flow (AI policy, chain, fallback recipient), plus a dry-run of the
 * decision on a real invoice. Only the policy needs a save; switches and the chain are stored as
 * they change.
 */
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { ConfirmationModal, Loader } from '@insio/ui-toolkit';
import {
  ApprovalStatus,
  ApprovalType,
  AutomationKind,
  InvoiceStatus,
  Permission
} from '@insio/shared-types';
import {
  InvoiceApprovalSettingsDocument,
  PreviewInvoiceApprovalDocument,
  ResetInvoiceApprovalSettingsDocument,
  UpdateInvoiceApprovalSettingsDocument
} from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { AutomationHelpModal } from './AutomationHelpModal';
import { ApprovalPreviewResult } from './ApprovalPreviewResult';
import { InvoicePickerModal } from './InvoicePickerModal';
import { ApprovalFlowSection } from './approval-flow/ApprovalFlowSection';
import { useApprovalSettingsForm } from './approval-flow/use-approval-settings-form';
import type { LifecycleStepRef } from './types';

const TRANSLATION_PREFIX = 'systemSettings.automation';

export interface InvoiceApprovalSettingsSectionProps {
  /** Opens another lifecycle step; the skipped notice links the next one. */
  onOpenStep?: (step: LifecycleStepRef) => void;
}

export function InvoiceApprovalSettingsSection({
  onOpenStep
}: InvoiceApprovalSettingsSectionProps) {
  const t = useFeatureTranslation(TRANSLATION_PREFIX);
  const form = useApprovalSettingsForm({
    settingsQuery: InvoiceApprovalSettingsDocument,
    selectSettings: data => data.invoiceApprovalSettings,
    updateMutation: UpdateInvoiceApprovalSettingsDocument,
    resetMutation: ResetInvoiceApprovalSettingsDocument,
    selectReset: data => data.resetInvoiceApprovalSettings,
    approvalType: ApprovalType.INVOICE_APPROVAL,
    approvePermission: Permission.INVOICES_APPROVE,
    translationPrefix: TRANSLATION_PREFIX
  });
  const { settings, loading, error, flowProps } = form;
  const [runPreview, { data: previewData, loading: isPreviewing, error: previewError }] =
    useMutation(PreviewInvoiceApprovalDocument);

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isResultDismissed, setIsResultDismissed] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const handleOpenPreview = () => {
    if (flowProps.isReadOnly) return;
    setIsPickerOpen(true);
  };

  const handlePreview = async (invoiceId: string) => {
    // The dry-run needs rules.manage, like every change of the rules.
    if (flowProps.isReadOnly) return;
    setIsResultDismissed(false);
    try {
      await runPreview({ variables: { invoiceId } });
    } catch {
      // Surfaced by the result panel via previewError — the mutation error is the single source.
    }
  };

  const preview = previewData?.previewInvoiceApproval;
  // The decision is shown as the invoice status it leads to; a referral waits for approval.
  const previewOutcome = preview && {
    ...preview,
    approvalStatus:
      preview.approvalStatus === ApprovalStatus.FOR_REVIEW
        ? InvoiceStatus.FOR_APPROVAL
        : preview.approvalStatus
  };
  const previewFootnote = preview
    ? preview.approverProposal === 'PROPOSED'
      ? t('preview.proposedApprover', {
          name: preview.proposedApproverName ?? t('preview.unknownApprover')
        })
      : preview.approverProposal === 'ANYONE_WITH_PERMISSION'
        ? t('preview.anyApprover')
        : preview.approverProposal === 'NO_SUITABLE_CANDIDATE'
          ? t('preview.noSuitableCandidate')
          : null
    : null;

  if (loading && !settings) {
    return <Loader.Section />;
  }

  if (error) {
    return <p className='text-sm text-error-text'>{t('loadError')}</p>;
  }

  return (
    <>
      <ApprovalFlowSection
        document='invoice'
        translationPrefix={TRANSLATION_PREFIX}
        testIdPrefix='invoice-approval'
        {...flowProps}
        isPreviewing={isPreviewing}
        onOpenPreview={handleOpenPreview}
        onOpenHelp={() => setIsHelpOpen(true)}
        previewResult={
          !isResultDismissed && (
            <ApprovalPreviewResult
              isRunning={isPreviewing}
              hasError={previewError != null}
              result={previewOutcome}
              onDismiss={() => setIsResultDismissed(true)}
              footnote={previewFootnote}
            />
          )
        }
        onOpenStep={onOpenStep}
      />

      <ConfirmationModal
        open={form.isResetConfirmOpen}
        title={t('resetConfirm.title')}
        message={t('resetConfirm.message')}
        confirmLabel={t('resetConfirm.confirm')}
        cancelLabel={t('resetConfirm.cancel')}
        variant='warning'
        loading={form.isResetting}
        onConfirm={form.handleReset}
        onClose={form.closeResetConfirm}
        testId='automation-reset-confirm'
      />

      <AutomationHelpModal
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        kind={AutomationKind.INVOICE_APPROVAL}
        title={t('title')}
      />

      <InvoicePickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handlePreview}
        title={t('preview.title')}
        description={t('preview.description')}
        testId='automation-preview-picker'
      />
    </>
  );
}
